"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import Tabs, { TAB_STYLE, TabItem } from "@/components/common/Tab";
import SubpageBackground from "@/components/common/SubpageBackground";
import PageTitle from "@/components/common/PageTitle";

export default function ProductsLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  // Hydration safety: randomized decorative particles should render only after mount.
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Canonical product order: HackGrid → SHRED → Q-SUITE → DarkSight. Keep in
  // sync with Header.tsx productItems, Footer.tsx and the home page sections
  // (the home page drops DarkSight but keeps the same order).
  //
  // Tab labels are the bare product names, not the full product titles — the
  // page title above already spells out the descriptor ("HACKGRID Cyber Range",
  // "DARKSIGHT Threat Intelligence"), so repeating it in the tab row only ate
  // horizontal space and forced the row to scroll.
  const tabs: TabItem[] = [
    {
      id: "1",
      name: "HACKGRID",
      value: "/products/hackgrid",
      href: "/products/hackgrid",
    },
    {
      id: "2",
      name: "SHRED",
      value: "/products/shred",
      href: "/products/shred",
    },
    {
      id: "3",
      name: "Q-SUITE",
      value: "/products/q-suite",
      href: "/products/q-suite",
    },
    {
      id: "4",
      name: "DARKSIGHT",
      value: "/products/darksight",
      href: "/products/darksight",
    },
  ];

  // The four product tabs (Q-Suite, HackGrid, SHRED, DarkSight) share the hexagon/
  // tech background "image 1" at 20% opacity; other pages keep the angular pattern.
  const usesHexBackground =
    pathname.includes("hackgrid") ||
    pathname.includes("darksight") ||
    pathname.includes("q-suite") ||
    pathname.includes("shred");

  // Determine the title based on the current path. These carry the full product
  // titles in their brand casing (the tab row above is the all-caps short form).
  const getTitle = () => {
    if (pathname.includes("hackgrid")) return "HackGrid Cyber Range";
    if (pathname.includes("darksight")) return "DarkSight - Threat Intelligence";
    if (pathname.includes("q-suite")) return "Q-SUITE";
    if (pathname.includes("shred")) return "SHRED Platform";
    return "Products";
  };

  const title = getTitle();

  return (
    <div className="relative min-h-screen flex flex-col bg-background pb-6 sm:pb-[40px] overflow-hidden">
      {/* Decorative background moved to `next/image` to reduce CLS. */}
      {usesHexBackground ? (
        <SubpageBackground
          src="/images/Product/products-bg.png"
          width={1024}
          height={683}
          className="opacity-20"
        />
      ) : (
        <SubpageBackground />
      )}

      {/* Additional animated overlay effects */}
      <div className="absolute inset-0 pointer-events-none z-[1]">
        {/* Scanning lines */}
        <div className="absolute inset-0 opacity-10">
          <div className="scan-line-h-product" />
          <div className="scan-line-v-product" />
        </div>

        {/* Floating particles */}
        {isMounted
          ? [...Array(10)].map((_, i) => (
              <div
                key={i}
                className="product-particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  animationDuration: `${5 + Math.random() * 3}s`,
                }}
              />
            ))
          : null}
      </div>

      {/* Foreground Content */}
      <div className="relative z-10 flex flex-col flex-1">
        {/* Title and Tab Section. Match the body content's responsive padding
            (px-4 sm:px-6 lg:px-8 inside max-w-[1300px]) so the accent bar, tab
            row and underline line up with the page content at every width —
            px-8 alone only aligned at lg and left the title/tabs inset 16px too
            far on mobile. */}
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <section className="w-full max-w-[1300px] mx-auto pb-6 pt-[112px] md:pt-[128px] md:pb-[48px]">
            {/* "DarkSight - Threat Intelligence" is the one title here that
                wraps below `sm` — 413px of ink in the 337px column at 390px —
                hence mobileLines={2}. "HackGrid Cyber Range" clears it by only
                28px (309 of 337), so re-measure both if this copy changes. */}
            <PageTitle className="mb-4 md:mb-6" mobileLines={2}>
              {title}
            </PageTitle>

            {/* Shared Tabs: a left-aligned underline row that scrolls sideways
                (auto-centering the active tab) when the labels don't fit — the
                same behaviour every other page's tab bar now uses. With the
                short labels the four tabs fit without scrolling from ~430px up.
                The first tab hugs the left margin so it lines up with the accent
                bar and page content. */}
            <div className="nav-tabs-wrapper">
              <Tabs
                tabs={tabs}
                currentValue={pathname}
                style={TAB_STYLE.STYLE_1}
                showContent={false}
                useRouter={true}
                customClassName="[&>a:first-child]:!pl-0"
              />
            </div>
          </section>
        </div>

        {/* Page Content */}
        <main key={pathname} className="flex-1 w-full page-content-enter">
          {children}
        </main>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        .scan-line-h-product {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(236, 25, 56, 0.5),
            transparent
          );
          animation: scan-h-product 10s linear infinite;
        }

        .scan-line-v-product {
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          width: 2px;
          background: linear-gradient(
            180deg,
            transparent,
            rgba(236, 25, 56, 0.5),
            transparent
          );
          animation: scan-v-product 12s linear infinite;
          animation-delay: 2s;
        }

        @keyframes scan-h-product {
          0% {
            top: 0%;
          }
          100% {
            top: 100%;
          }
        }

        @keyframes scan-v-product {
          0% {
            left: 0%;
          }
          100% {
            left: 100%;
          }
        }

        .product-particle {
          position: absolute;
          width: 4px;
          height: 4px;
          background: rgba(236, 25, 56, 0.5);
          border-radius: 50%;
          animation: product-float infinite ease-in-out;
        }

        @keyframes product-float {
          0%,
          100% {
            transform: translate(0, 0) scale(1);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          50% {
            transform: translate(30px, -60px) scale(1.5);
            opacity: 0.6;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translate(60px, -120px) scale(1);
            opacity: 0;
          }
        }
        /* The title's own transition lives in PageTitle, so it is defined once
           for every header that uses it instead of being pasted per layout. */

        /* Nav tabs wrapper — fade up on mount */
        .nav-tabs-wrapper {
          animation: navFadeUp 0.5s ease-out 0.15s both;
        }

        @keyframes navFadeUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Page content entrance animation on route change. The short delay lets
           the title lead and the body follow, instead of two animations of
           different length starting on the same frame and reading as noise. */
        .page-content-enter {
          animation: pageEnter 0.4s ease-out 60ms both;
        }

        @keyframes pageEnter {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* globals.css only silences a handful of named effects, so these have to
           opt out here. */
        @media (prefers-reduced-motion: reduce) {
          .page-content-enter,
          .nav-tabs-wrapper {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}
